import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import BaseModal from 'shared/components/base-modal'
import CloseIcon from 'shared/icons/close-icon'
import { DataFile } from 'shared/types/datafile-type'
import { useCoursesOfCourseBundle } from 'modules/school/hooks/use-courses-of-course-bundle'
import defaultCourseCover from '../../../../../public/images/default-course-cover.png'
import { SchoolEnrollmentId } from '../../types/school-enrollments-interface'
import { CourseCard } from './course-card'
import { CourseCardsSkeleton } from './course-card-skeleton'

export interface SchoolCourseBundleModalModalProps {
  isOpened: boolean
  onClose: () => void
  schoolEnrollmentId: SchoolEnrollmentId | null
  name: string | null
  imagePath: DataFile['path'] | null
}

function SchoolCourseBundleModal({
  isOpened,
  onClose,
  schoolEnrollmentId,
  name,
  imagePath,
}: SchoolCourseBundleModalModalProps): ReactNode {
  const { t } = useTranslation()

  const { coursesOfCourseBundle } = useCoursesOfCourseBundle({ schoolEnrollmentId })

  return (
    <BaseModal
      className="relative rounded-2xl! bg-background-primary"
      opened={isOpened}
      onClose={onClose}
    >
      <CloseIcon
        onClick={onClose}
        className="absolute top-2 right-2 z-10 size-8 cursor-pointer rounded-full bg-background-secondary p-2 lg:top-0 lg:right-[unset] lg:left-full lg:ml-3"
      />
      <div data-testid="bundle-modal-content">
        <div
          data-testid="bundle-modal-hero-frame"
          className="relative overflow-hidden rounded-t-2xl before:absolute before:inset-0 before:rounded-t-2xl before:bg-linear-to-b before:from-transparent before:to-[rgb(var(--text-primary)/1)]"
        >
          <div className="relative min-h-50 w-full sm:h-75">
            <img
              data-testid="bundle-modal-hero-image"
              className="absolute inset-0 block size-full object-cover object-center"
              src={imagePath || defaultCourseCover.src}
              alt=""
            />
          </div>
          <h3 className="absolute bottom-3 left-3 text-xl font-bold text-background-secondary sm:bottom-8 sm:left-8 sm:text-2xl">
            {name}
          </h3>
        </div>
        <div className="p-5 md:p-8">
          <div className="flex justify-between">
            <span className="text-lg font-bold text-text-primary sm:text-3xl">
              {t('school.course_bundle_modal.courses_label')}
            </span>
          </div>
          <div className={`mt-4 flex flex-col gap-12 sm:mt-9`}>
            {coursesOfCourseBundle ? (
              coursesOfCourseBundle?.map((data, index) => <CourseCard key={index} data={data} />)
            ) : (
              <CourseCardsSkeleton />
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  )
}

export default SchoolCourseBundleModal
